/**
 * SlaReportCard — Report SLA mensile in /admin/system/history.
 * Download PDF del periodo scelto + impostazioni invio automatico
 * mensile (toggle + email multiple) + invio manuale immediato.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Mail, Loader2, Save, X, Plus, CalendarRange } from "lucide-react";
import { API } from "../../config";

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setDate(0); // last day of previous month
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const SlaReportCard = () => {
  const [{ year, month }, setPeriod] = useState(prevMonth());
  const [settings, setSettings] = useState({ enabled: false, recipients: [] });
  const [emailInput, setEmailInput] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/sla-report/settings`);
      setSettings({ enabled: r.data.enabled, recipients: r.data.recipients || [] });
    } catch { /* card resta con default */ }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/sla-report/pdf?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SLA_Report_LoJack_${year}-${String(month).padStart(2, "0")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report SLA scaricato");
    } catch {
      toast.error("Generazione report fallita");
    } finally { setDownloading(false); }
  };

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Email non valida"); return; }
    if (!settings.recipients.includes(e)) {
      setSettings(s => ({ ...s, recipients: [...s.recipients, e] }));
    }
    setEmailInput("");
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/sla-report/settings`, settings);
      setSettings({ enabled: r.data.enabled, recipients: r.data.recipients });
      toast.success(r.data.enabled
        ? `Invio automatico ATTIVO (1° del mese) a ${r.data.recipients.length} destinatari`
        : "Impostazioni salvate — invio automatico disattivato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Inviare subito il Report SLA di ${month}/${year} a: ${settings.recipients.join(", ")}?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/sla-report/send-now?year=${year}&month=${month}`);
      toast.success(`Report inviato a ${r.data.recipients.length} destinatari (uptime ${r.data.uptime_pct}%)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio fallito");
    } finally { setSending(false); }
  };

  const monthValue = `${year}-${String(month).padStart(2, "0")}`;

  return (
    <div className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-5 mb-6" data-testid="sla-report-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-white text-sm font-semibold flex items-center gap-2">
          <CalendarRange size={15} className="text-lojack-blue-400" />
          Report SLA — Managed Service
        </h3>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        PDF con carta intestata: disponibilità, incidenti, backup, sync Salesforce e contratti firmati del mese.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <input
          type="month"
          value={monthValue}
          max={new Date().toISOString().slice(0, 7)}
          min="2025-01"
          onChange={e => {
            const [y, m] = e.target.value.split("-").map(Number);
            if (y && m) setPeriod({ year: y, month: m });
          }}
          className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:border-lojack-blue-500 outline-none"
          data-testid="sla-month-input"
        />
        <button
          onClick={download}
          disabled={downloading}
          className="flex items-center gap-1.5 px-3 py-2 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg text-xs font-semibold transition"
          data-testid="sla-download-btn"
        >
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
          Scarica PDF
        </button>
        <button
          onClick={sendNow}
          disabled={sending || settings.recipients.length === 0}
          title={settings.recipients.length === 0 ? "Aggiungi prima almeno un destinatario" : undefined}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-700 hover:bg-white/5 disabled:opacity-40 rounded-lg text-xs text-gray-300 transition"
          data-testid="sla-send-now-btn"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
          Invia ora via email
        </button>
      </div>

      <div className="border-t border-gray-800 pt-4">
        <label className="flex items-center gap-2.5 cursor-pointer mb-3" data-testid="sla-auto-toggle-label">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
            className="accent-lojack-blue-500 w-4 h-4"
            data-testid="sla-auto-toggle"
          />
          <span className="text-xs text-gray-300">
            <strong className="text-white">Invio automatico mensile</strong> — il report del mese precedente
            parte il 1° di ogni mese (~8:00) ai destinatari qui sotto
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="sla-recipients-list">
          {settings.recipients.map(r => (
            <span key={r} className="flex items-center gap-1 bg-white/5 border border-gray-700 rounded-full px-2.5 py-1 text-[11px] text-gray-200">
              {r}
              <button
                onClick={() => setSettings(s => ({ ...s, recipients: s.recipients.filter(x => x !== r) }))}
                className="text-gray-500 hover:text-lojack-red"
                data-testid={`sla-remove-${r}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
          {settings.recipients.length === 0 && (
            <span className="text-gray-600 text-[11px] italic">Nessun destinatario configurato</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addEmail()}
            placeholder="aggiungi email destinatario (es. a.zorzutti@lojack.it)"
            className="flex-1 bg-black border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:border-lojack-blue-500 outline-none"
            data-testid="sla-email-input"
          />
          <button onClick={addEmail} className="p-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-white/5 transition" data-testid="sla-add-email-btn">
            <Plus size={13} />
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/15 disabled:opacity-50 rounded-lg text-xs font-semibold text-white transition"
            data-testid="sla-save-settings-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Salva impostazioni
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlaReportCard;
